import { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebookF } from "@fortawesome/free-brands-svg-icons/faFacebookF";
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons/faLinkedinIn";
import { faTiktok } from "@fortawesome/free-brands-svg-icons/faTiktok";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons/faXTwitter";
import { faYoutube } from "@fortawesome/free-brands-svg-icons/faYoutube";
import ContactForm from "../ContactForm/ContactForm";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons/faMapMarkerAlt";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faClock } from "@fortawesome/free-solid-svg-icons/faClock";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import badgeFooter from "../assets/logos/badgefooter.jpg";
import Brokers_ireland from "../assets/Home/brokers_ireland.png";
import "./Footer.css";

const POLICY_URLS = Object.freeze({
  terms:
    "https://documents.apollofinancial.ie/polices_pdfs/Terms-of-Business-31st-July-2025.pdf",
  remuneration:
    "https://documents.apollofinancial.ie/polices_pdfs/Remuneration-Document.pdf",
  privacy:
    "https://documents.apollofinancial.ie/polices_pdfs/Data-Protection-Policy-Updated.pdf",
});

const captchaSiteKey =
  window._env_?.VITE_CAPTCHA_SITE_KEY !== "${VITE_CAPTCHA_SITE_KEY}"
    ? window._env_.VITE_CAPTCHA_SITE_KEY
    : import.meta.env.VITE_CAPTCHA_SITE_KEY;

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src="https://www.google.com/recaptcha/enterprise.js?render=${captchaSiteKey}"]`
    );
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${captchaSiteKey}`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSuccess = useCallback(() => {
    console.log("Form submitted successfully from Footer page");
  }, []);

  const handleError = useCallback((error: Error) => {
    console.error("Form submission error from Footer page:", error);
  }, []);

  const API_BASE_URL = useMemo(
    () =>
      window._env_?.VITE_API_BASE_URL !== "${VITE_API_BASE_URL}"
        ? window._env_.VITE_API_BASE_URL
        : "/api",
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSubmitted(false);

    if (!email) {
      setIsLoading(false);
      return;
    }

    try {
      if (!window.grecaptcha?.enterprise) {
        throw new Error("CAPTCHA not loaded");
      }

      await new Promise<void>((resolve) =>
        window.grecaptcha.enterprise.ready(resolve)
      );

      const captchaToken = await window.grecaptcha.enterprise.execute(
        captchaSiteKey,
        { action: "NEWSLETTER_SUBSCRIBE" }
      );

      const payload = {
        email: email,
        source: "apollo",
        g_recaptcha_response: captchaToken,
      };

      const response = await fetch(`${API_BASE_URL}/external/api/subscribe/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.email && Array.isArray(data.email) && data.email.length > 0) {
          const originalMessage = data.email[0];
          const formattedMessage =
            originalMessage.charAt(0).toUpperCase() +
            originalMessage.slice(1).replace(/\.$/, "") +
            "!";
          setErrorMessage(formattedMessage);
        } else {
          throw new Error(data.message || "Subscription failed");
        }
        return;
      }

      setSubmitted(true);
      setEmail("");
      setTimeout(() => setSubmitted(false), 5000);
      handleSuccess();
    } catch (error) {
      console.error("Error submitting form:", error);
      handleError(error as Error);
      setErrorMessage("An error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-column">
          <div className="footer-section">
            <h3>Apollo Financial Services</h3>
            <p>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Thomastown House,
              Thomastown, Duleek <p>Co. Meath, A92 X362, Ireland.</p>
            </p>
            <p>
              <FontAwesomeIcon icon={faPhone} /> 01 689 5066
            </p>
            <p>
              <FontAwesomeIcon icon={faEnvelope} /> info@apollofinancial.ie
            </p>
            <div>
              <p>
                <FontAwesomeIcon icon={faClock} />{" "}
                <strong>Opening Hours</strong>
              </p>
              <p>Monday to Friday:</p>
              <p>9:00 a.m. – 1:00 p.m.</p>
              <p>2:00 p.m. – 5:00 p.m.</p>
              <p style={{ fontStyle: "italic" }}>Closed on bank holidays</p>
            </div>
          </div>

          <div className="footer-section" style={{ marginTop: "1rem" }}>
            <h3 style={{ marginBottom: "-10px" }}>Follow Us</h3>
            <div className="social-icons-footer">
              <a
                href="https://www.facebook.com/ApolloFinancialServicesLtd"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
              <a
                href="https://www.instagram.com/apollofinancialservices/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <a
                href="https://ie.linkedin.com/company/apollo-financial-services-limited"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faLinkedinIn} />
              </a>
              <a
                href="https://x.com/ApolloFServices"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faXTwitter} />
              </a>
              <a
                href="https://www.tiktok.com/@apollofservices"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faTiktok} />
              </a>
              <a
                href="https://www.youtube.com/@ApolloFinancialServicesLTD"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faYoutube} />
              </a>
            </div>

            <div className="footer-section-badge">
              <div className="badge-brokers-container">
                <img
                  className="badge-footer-img"
                  style={{ height: "150px", width: "150px" }}
                  src={badgeFooter}
                  alt="Logo"
                />
                <img
                  className="brokers-ireland-img mobile-only"
                  style={{ height: "150px", width: "250px" }}
                  src={Brokers_ireland}
                  alt="Logo"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="footer-column">
          <div className="footer-section">
            <h3>Quick Links</h3>
            <ul>
              <li>
                <Link to="/about/" onClick={scrollToTop}>
                  <FontAwesomeIcon icon={faArrowRight} /> About Us
                </Link>
              </li>
              <li>
                <Link to="/faq/" onClick={scrollToTop}>
                  <FontAwesomeIcon icon={faArrowRight} /> FAQs
                </Link>
              </li>
              <li>
                <Link to="/contact/" onClick={scrollToTop}>
                  <FontAwesomeIcon icon={faArrowRight} /> Contact Us
                </Link>
              </li>
              {/* <li>
                <Link to="/careers">
                  <FontAwesomeIcon icon={faArrowRight} /> Careers
                </Link>
              </li> */}
              <li>
                <Link
                  to={POLICY_URLS.remuneration}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faArrowRight} /> Remuneration Document
                </Link>
              </li>
              <li>
                <Link
                  to={POLICY_URLS.terms}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faArrowRight} /> Terms of Business
                </Link>
              </li>
              <li>
                <Link
                  to={POLICY_URLS.privacy}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faArrowRight} /> Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h3 style={{ marginBottom: "0rem" }}>Stay Updated</h3>
            <p>Subscribe to our newsletter for the latest updates.</p>
            <form className="newsletter-form" onSubmit={handleSubmit}>
              <div className="newsletter-input-group">
                <input
                  type="email"
                  placeholder="Your Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" disabled={isLoading}>
                  {isLoading ? "Subscribing..." : "Subscribe"}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              <div className="newsletter-messages">
                {submitted && (
                  <div className="newsletter-success">
                    Thank you for subscribing! We will be in touch.
                  </div>
                )}
                {errorMessage && (
                  <div className="newsletter-error">{errorMessage}</div>
                )}
              </div>
            </form>
            <img
              className="brokers-ireland-img desktop-only"
              style={{ height: "160px", width: "300px", marginTop: "10px" }}
              src={Brokers_ireland}
              alt="Logo"
            />
          </div>
        </div>

        <div className="footer-column">
          <div className="footer-section">
            <h3>Get in Touch</h3>
            <ContactForm
              onSuccess={handleSuccess}
              onError={handleError}
              footer={true}
            />
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <p>
          Apollo Financial Services Limited is regulated by the Central Bank of
          Ireland. Registered Office Thomastown House, Thomastown, Duleek, Co.
          Meath, A92 X362, Ireland
        </p>
        <p>
          Apollo Financial Services Limited is registered in Ireland number
          598973. &copy; Apollo Financial Services Ltd.{" "}
          {new Date().getFullYear()}
        </p>
        <p>
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0ea5e9", textDecoration: "underline" }}
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0ea5e9", textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          apply.
        </p>
      </div>
      {showScrollTop && (
        <button
          className="scroll-to-top-floating"
          onClick={scrollToTop}
          aria-label="Scroll to Top"
        >
          <FontAwesomeIcon icon={faChevronUp} />
        </button>
      )}
    </footer>
  );
};

export default Footer;
